import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import styles from '../../style.module.css'
import {
	fetchClientMarketPricings,
	upsertClientMarketPricing,
	deleteClientMarketPricing,
} from '../../pricingApi'
import { usePricingStore } from '../../pricingStore'

const ClientPricingEditor = ({ match }) => {
	const { markets, activePricingType } = usePricingStore()
	const clientId = match && match.params ? match.params.clientId : null
	const [clientMarketPricings, setClientMarketPricings] = useState([])
	const [activeMarket, setActiveMarket] = useState(null)
	const [form, setForm] = useState({
		licensePrice: '',
		compensation: '',
		furtherDamages: '',
		currency: '',
		manipulatedFee: '',
		ceaseAndDesistFee: '',
		reminderOneFee: '',
		reminderTwoFee: '',
		finalWarningFee: '',
	})
	const [status, setStatus] = useState('idle')
	const [errorMessage, setErrorMessage] = useState('')

	const refreshClientMarketPricings = async () => {
		if (!clientId) return
		const clientPricings = await fetchClientMarketPricings(
			clientId,
			activePricingType,
		)
		setClientMarketPricings(clientPricings || [])
		if (!activeMarket && markets.length) setActiveMarket(markets[0].market)
	}

	useEffect(() => {
		refreshClientMarketPricings()
	}, [clientId, markets, activePricingType])

	useEffect(() => {
		if (!activeMarket) return
		const override = clientMarketPricings.find((o) => o.market === activeMarket)
		setForm({
			currency: (override && override.currency) || '',
			licensePrice:
				override && override.licensePrice != null ? override.licensePrice : '',
			compensation:
				override && override.compensation != null ? override.compensation : '',
			furtherDamages:
				override && override.furtherDamages != null
					? override.furtherDamages
					: '',
			manipulatedFee:
				override && override.manipulatedFee != null
					? override.manipulatedFee
					: '',
			ceaseAndDesistFee:
				override && override.ceaseAndDesistFee != null
					? override.ceaseAndDesistFee
					: '',
			reminderOneFee:
				override && override.reminderOneFee != null
					? override.reminderOneFee
					: '',
			reminderTwoFee:
				override && override.reminderTwoFee != null
					? override.reminderTwoFee
					: '',
			finalWarningFee:
				override && override.finalWarningFee != null
					? override.finalWarningFee
					: '',
		})
	}, [activeMarket, clientMarketPricings, markets])

	const update = (field, value) => setForm((f) => ({ ...f, [field]: value }))

	const getClientMarketPlaceholder = (field) => {
		if (!activeMarket) {
			return ''
		}
		const marketPricing = markets.find((m) => m.market === activeMarket)
		if (!marketPricing) {
			return ''
		}
		return marketPricing[field] || ''
	}

	const saveClientPricing = async () => {
		if (!clientId || !activeMarket) return
		setStatus('saving')
		setErrorMessage('')
		try {
			const payload = {
				currency: (form.currency && form.currency.trim()) || null,
				licensePrice:
					form.licensePrice === '' ? null : parseFloat(form.licensePrice),
				compensation:
					form.compensation === '' ? null : parseFloat(form.compensation),
				furtherDamages:
					form.furtherDamages === '' ? null : parseFloat(form.furtherDamages),
				manipulatedFee:
					form.manipulatedFee === '' ? null : parseFloat(form.manipulatedFee),
				ceaseAndDesistFee:
					form.ceaseAndDesistFee === ''
						? null
						: parseFloat(form.ceaseAndDesistFee),
				reminderOneFee:
					form.reminderOneFee === '' ? null : parseFloat(form.reminderOneFee),
				reminderTwoFee:
					form.reminderTwoFee === '' ? null : parseFloat(form.reminderTwoFee),
				finalWarningFee:
					form.finalWarningFee === '' ? null : parseFloat(form.finalWarningFee),
			}
			if (
				![
					payload.currency,
					payload.licensePrice,
					payload.compensation,
					payload.furtherDamages,
					payload.manipulatedFee,
					payload.ceaseAndDesistFee,
					payload.reminderOneFee,
					payload.reminderTwoFee,
					payload.finalWarningFee,
				].some((v) => v !== null && v !== '')
			) {
				await removeClientPricing()
				return
			}

			// Validate required fields
			if (!payload.currency || payload.currency.trim() === '') {
				setStatus('error')
				setErrorMessage('Currency is required')
				return
			}
			if (payload.licensePrice === null) {
				setStatus('error')
				setErrorMessage('License Price is required')
				return
			}

			await upsertClientMarketPricing(
				clientId,
				activeMarket,
				activePricingType,
				payload,
			)
			await refreshClientMarketPricings()
			setStatus('saved')
			setTimeout(() => setStatus('idle'), 2000)
		} catch (e) {
			console.error(e)
			setStatus('error')
			setErrorMessage('Failed to save pricing. Please try again.')
		}
	}

	const removeClientPricing = async () => {
		if (!clientId || !activeMarket) return
		setStatus('saving')
		try {
			await deleteClientMarketPricing(clientId, activeMarket, activePricingType)
			await refreshClientMarketPricings()
			setStatus('saved')
			setTimeout(() => setStatus('idle'), 2000)
		} catch (e) {
			console.error(e)
			setStatus('error')
		}
	}

	return (
		<div className={styles.container}>
			<h3 style={{ marginTop: 0 }}>Partner Pricing ({activePricingType})</h3>
			{!markets.length && (
				<div className={styles.card}>No markets available yet.</div>
			)}
			{markets.length > 0 && (
				<div className={styles.card}>
					<div className={styles.cardHeader}>
						<h4 className={styles.cardTitle}>Markets</h4>
					</div>
					<div className={styles.tabs}>
						{markets.map((m) => {
							const hasClientPricing = clientMarketPricings.find(
								(o) => o.market === m.market,
							)
							return (
								<div
									key={m.market}
									className={
										activeMarket === m.market
											? `${styles.tab} ${styles.active}`
											: styles.tab
									}
									onClick={() => setActiveMarket(m.market)}
									style={{
										...(!hasClientPricing ? { paddingTop: '10px' } : {}),
										borderLeft: hasClientPricing
											? '4px solid #5cb85c'
											: '4px solid #ccc',
									}}
								>
									{m.market}
									{hasClientPricing && (
										<span className={`${styles.badge} ${styles.override}`}>
											Configured
										</span>
									)}
								</div>
							)
						})}
					</div>
					{activeMarket && (
						<div>
							<h4 className={styles.sectionTitle}>
								Market: {activeMarket}
								{clientMarketPricings.find((o) => o.market === activeMarket) ? (
									<span
										style={{
											marginLeft: '10px',
											fontSize: '0.9em',
											color: '#5cb85c',
											fontWeight: 'normal',
										}}
									>
										(Partner-specific pricing configured)
									</span>
								) : (
									<span
										style={{
											marginLeft: '10px',
											fontSize: '0.9em',
											color: '#999',
											fontWeight: 'normal',
										}}
									>
										(Not configured)
									</span>
								)}
							</h4>
							<div className={styles.formGroup}>
								<label>
									Currency <span style={{ color: 'red' }}>*</span>
								</label>
								<input
									value={form.currency}
									onChange={(e) =>
										update('currency', e.target.value.toUpperCase())
									}
									placeholder={getClientMarketPlaceholder('currency')}
									maxLength={3}
								/>
							</div>
							<div className={styles.formGroup}>
								<label>
									License Price <span style={{ color: 'red' }}>*</span>
								</label>
								<input
									placeholder={getClientMarketPlaceholder('licensePrice')}
									value={form.licensePrice}
									onChange={(e) => update('licensePrice', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<div className={styles.formGroup}>
								<label>Compensation</label>
								<input
									placeholder={getClientMarketPlaceholder('compensation')}
									value={form.compensation}
									onChange={(e) => update('compensation', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<div className={styles.formGroup}>
								<label>Further Damages</label>
								<input
									placeholder={getClientMarketPlaceholder('furtherDamages')}
									value={form.furtherDamages}
									onChange={(e) => update('furtherDamages', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<div className={styles.formGroup}>
								<label>Manipulated Fee</label>
								<input
									placeholder={getClientMarketPlaceholder('manipulatedFee')}
									value={form.manipulatedFee}
									onChange={(e) => update('manipulatedFee', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<p style={{ fontSize: '0.85em', color: '#999' }}>
								Reminder fees: leave empty to use market defaults
							</p>
							<div className={styles.formGroup}>
								<label>Cease &amp; Desist Fee</label>
								<input
									placeholder={getClientMarketPlaceholder('ceaseAndDesistFee')}
									value={form.ceaseAndDesistFee}
									onChange={(e) => update('ceaseAndDesistFee', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<div className={styles.formGroup}>
								<label>Reminder One Fee</label>
								<input
									placeholder={getClientMarketPlaceholder('reminderOneFee')}
									value={form.reminderOneFee}
									onChange={(e) => update('reminderOneFee', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<div className={styles.formGroup}>
								<label>Final Warning Fee</label>
								<input
									placeholder={getClientMarketPlaceholder('finalWarningFee')}
									value={form.finalWarningFee}
									onChange={(e) => update('finalWarningFee', e.target.value)}
									type='number'
									min='0'
									step='0.01'
								/>
							</div>
							<div className={styles.actions}>
								<button
									className={styles.saveButton}
									onClick={saveClientPricing}
									disabled={status === 'saving'}
								>
									Save Market Pricing
								</button>
								<button
									className={styles.saveButton}
									onClick={removeClientPricing}
									disabled={status === 'saving'}
									style={{ background: '#d9534f' }}
								>
									Remove Market Pricing
								</button>
								{status === 'saving' && (
									<span className={styles.statusSaving}>Saving...</span>
								)}
								{status === 'saved' && (
									<span className={styles.statusSuccess}>Saved!</span>
								)}
								{status === 'error' && (
									<span className={styles.statusError}>
										{errorMessage || 'Error'}
									</span>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	)
}

export default withRouter(ClientPricingEditor)
